import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { default as SelectComponent } from "react-select";
import DatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import es from "date-fns/locale/es";
import Multiselect from "multiselect-react-dropdown";
import { FILTER_TYPES, INPUT_TYPES } from "../../constants";
import { FilterType } from "../../types";
import "./style.css";
import AmountRangeComponent from "../amountRange/amountRange";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import InputColumnFilterComponent from "../inputColumnFilter/inputColumnFilter";
import { useEsTelefono } from "../../utils/esTelefono";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import MultiValueInput from "../multiValueInput/multiValueInput";

registerLocale("es", es); // Registra el idioma español
setDefaultLocale("es"); // Establece el español como idioma predeterminado

export interface FilterOption {
	id: string;
	label: string;
	value: string;
}

export interface Column {
	/**
	 * Id to identify the Column
	 */
	id: string;
	/**
	 * Label to show in the Column
	 */
	header: string | any;
	/**
	 * Object Property to access data
	 */
	accessor: string;
	/**
	 * Optional render ?, can be HTMLElements such as Buttons, Checkboxes, etc.
	 */
	render?: (data: any) => React.ReactNode;
	/**
	 * Optional render ?, can be HTMLElements such as Buttons, Checkboxes, etc.
	 */
	renderSubRow?: (data: any) => React.ReactNode;
	/**
	 * Optional function to apply custom styles
	 */
	overrideCellStyle?: (data: any) => React.CSSProperties;
	/**
	 * Optional function to apply custom styles in table headers
	 */
	overrideHeaderStyle?: (data: any) => React.CSSProperties;
	/**
	 * Optional function to override table data classes
	 */
	overrideCellClasses?: string;
	/**
	 * Optional function to override table headers classes
	 */
	overrideHeaderClasses?: string;
	/**
	 * En la vista de TARJETAS (telefono), esta columna se dibuja arriba a la
	 * derecha, en la misma linea del titulo, en vez de bajar con los demas
	 * datos. Pensado para la factura. (Esteban, 2026-09-20)
	 */
	enCabeceraEnTelefono?: boolean;
	/**
	 * En la vista de TARJETAS (telefono), esta columna es el TITULO de la tarjeta.
	 *
	 * Sin marcar ninguna, el titulo es la primera columna con etiqueta, que no
	 * siempre es la que sirve para reconocer el renglon (en Pagos es el "ID").
	 * (Esteban, 2026-09-20)
	 */
	tituloEnTelefono?: boolean;
	/**
	 * Que escribir como titulo de la tarjeta en telefono, cuando el valor de la
	 * columna no alcanza. En Pagos el titulo era la columna "Nombres", asi que
	 * el apellido nunca salia arriba. (Esteban, 2026-09-21)
	 */
	renderTituloEnTelefono?: (data: any) => React.ReactNode;
	/**
	 * Type of filter: "dropdown", "input", etc.
	 */
	filterType?: FilterType;
	/**
	 * Determine if filter: is active, if is active show controls otherwise hide it.
	 */
	filterEnabled?: boolean;
	/**
	 * Array of filter options for dropdowns
	 */
	filterOptions?: FilterOption[];
	/**
	 * Default options for filters
	 */
	defaultFilterValues?: FilterOption[];
	/**
	 * Default options for filters
	 */
	defaultFilterValue?: any;
	/**
	 * Manage date picker controller
	 */
	datePickerState?: {
		date: Date | null;
		setDate: (date: Date | null) => void
	};
	/**
	 * Manage dateRange controllers
	 */
	dateRangeState?: {
		start: Date | null;
		end: Date | null;
		setStart: (date: Date | null) => void;
		setEnd: (date: Date | null) => void
	};
	/**
	 * Manage quantity range controller
	 */
	quantityRangeState?: {
		option: string;
		setOption: (option: string) => void;
		quantity: number;
		setQuantity: (quantity: number) => void;
	};
}

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
	/**
	 * Columns to show in the Table
	 */
	columns: Column[];
	/**
	 * Data to show in the Table
	 */
	data: any[];
	/**
	 * Optional function for row click event
	 */
	onRowClick?: (data: any) => void;
	/**
	 * Filters to apply in the Table columns
	 */
	filters?: any;
	/**
	 * Optional function search data
	 */
	onFilterChange?: (columnId: string, value: any) => void;
	/**
	 * Optional flag to clear filters
	 */
	onClearFilters?: () => void;
	/**
	 * En telefono, dibujar una TARJETA por fila en vez de la tabla.
	 *
	 * Va por opcion y no siempre porque esta tabla la usan ocho pantallas y
	 * algunas ya resuelven lo suyo. Donde se enciende, se reaprovechan las mismas
	 * columnas: cada una aporta su etiqueta y su render, asi que las pastillas,
	 * los colores y los enlaces salen igual que en la tabla.
	 * (Esteban, 2026-09-20)
	 */
	tarjetasEnTelefono?: boolean;
}

/**
 * Common Table component for user interaction
 */
const CommonTable: React.FC<TableProps> = ({
	className,
	columns,
	data,
	onRowClick,
	filters,
	onFilterChange = (columnId: string, value: any) => { },
	onClearFilters = () => { },
	tarjetasEnTelefono = false
}) => {

	const esTelefono = useEsTelefono();
	const tableClass = classNames("tableComponent", className);
	const [filterStates, setFilterStates] = useState(
		columns.reduce((acc: any, column: Column) => {
			acc[column.id] = column.filterEnabled || false;
			return acc;
		}, {})
	);

	const isAnyFilterActive = () => {
		return Object.values(filterStates).some(value => value === true);
	};

	const handleClearFilterColumn = (columnId: string) => {
		setFilterStates((prevStates: any) => ({
			...prevStates,
			[columnId]: !prevStates[columnId],
		}));
		onFilterChange(columnId, null);
	};

	const handleClearAllFilterColumn = () => {
		setFilterStates((prevStates: any) => {
			// new object with all filters in status false
			const newStates = Object.keys(prevStates).reduce((acc: any, columnId: any) => {
				acc[columnId] = false;
				return acc;
			}, {});

			return newStates; // return new object
		});
		onClearFilters();
	};

	const renderFilterType = (
		filterType: FilterType,
		column: Column,
		filters: any,
		onFilterChange: (columnId: string, value: any) => void
	) => {
		switch (filterType) {
			case FILTER_TYPES.INPUT_TEXT: // Input TEXT filter
				const handleInputFilterChange = (value: string) => {
					onFilterChange(column.id, value);
				};
				return (
					<div style={{
						display: "flex",
						gap: "10px",
						width: "100%",
						justifyContent: "center",
						alignItems: "center"
					}}>
						<InputColumnFilterComponent
							onFilterChange={(value: string) => handleInputFilterChange(value)}
							type={INPUT_TYPES.INPUT_TEXT}
							placeholder={column.header}
						/>
					</div>
				);
			case FILTER_TYPES.INPUT_NUMBER: // Input NUMBER filter
				// return (<MultiValueInput />);
				const handleInputNumberFilterChange = (value: string) => {
					onFilterChange(column.id, value);
				};
				return (
					<div style={{
						display: "flex",
						gap: "10px",
						width: "100%",
						justifyContent: "center",
						alignItems: "center"
					}}>
						<InputColumnFilterComponent
							onFilterChange={(value: string) => handleInputNumberFilterChange(value)}
							type={INPUT_TYPES.INPUT_NUMBER}
							placeholder={column.header}
							defaultValue={column.defaultFilterValue || ""}
						/>
					</div>
				);
			case FILTER_TYPES.DROPDOWN: // Dropdown filter
				return (
					column.filterOptions && (
						<SelectComponent
							id={column.id}
							options={column.filterOptions}
							getOptionLabel={(option) => option.label}
							getOptionValue={(option) => option.value}
							onChange={(selectedOption) => {
								const value = selectedOption ? selectedOption.value : "";
								onFilterChange(column.id, value);
							}}
							styles={{
								menu: (provided) => ({
									...provided,
									width: "100%", // Asegura que el contenedor del select sea 100%
								}),
								container: (provided) => ({
									...provided,
									width: "100%", // Asegura que el contenedor del select sea 100%
								}),
								control: (provided) => ({
									...provided,
									width: "100%", // Asegura que el control del select sea 100%
								}),
								option: (provided, state) => ({
									...provided,
									display: "flex", // Para permitir el uso de flexbox
									alignItems: "center",
									justifyContent: "center", // Centrar el contenido
									textAlign: "center" // Centrar el texto
								}),
								singleValue: (provided) => ({
									...provided,
									textAlign: "center", // Centrar el valor seleccionado
								}),
							}}
							placeholder={`Select ${column.header}`}
						/>
					)
				);
			case FILTER_TYPES.MULTISELECT: // Input text multiselect
				return (
					column.filterOptions && (
						<Multiselect
							options={column.filterOptions}
							displayValue="label"
							emptyRecordMsg="No hay más opciones"
							onSelect={(selectedOptions) => {
								const selectedValues = selectedOptions.map((option: any) => option.value);
								onFilterChange(column.id, selectedValues);
							}}
							onRemove={(selectedOptions) => {
								const selectedValues = selectedOptions.map((option: any) => option.value);
								onFilterChange(column.id, selectedValues);
							}}
							selectedValues={
								filters[column.id]?.map((value: any) =>
									column.filterOptions?.find(option => option.value === value)
								) || (column.defaultFilterValues?.map((item: any) =>
									column.filterOptions?.find(option => option.value === item.value)
								).filter(Boolean) || [])
							}
						/>
					)
				);
			case FILTER_TYPES.DATE_PICKER: // Date picker filter
				return (
					<DatePicker
						selected={column.datePickerState?.date || null}
						onChange={(date) => {
							column.datePickerState?.setDate(date);
							onFilterChange(column.id, date ? date.toISOString().split("T")[0] : null);
						}}
						dateFormat="dd/MM/yyyy"
						placeholderText={`Select ${column.header}`}
						showYearDropdown // Muestra un desplegable para seleccionar el año
						showMonthDropdown // Muestra un desplegable para seleccionar el mes
						dropdownMode="select" // Usa un modo desplegable para la selección
						locale="es" // Establece el idioma aquí
					/>
				);
			case FILTER_TYPES.DATE_RANGE: // Date range filter
				return (
					<div style={{
						display: "flex",
						flexDirection: "column",
						gap: "10px",
						marginBottom: "5px"
					}}>
						<DatePicker
							className="filter-datepicker"
							selected={column.dateRangeState?.start || null}
							onChange={(date) => {
								column.dateRangeState?.setStart(date);
								const endDate = column.dateRangeState?.end ? column.dateRangeState.end.toISOString().split("T")[0] : null;

								if (!endDate || (date && date <= (column.dateRangeState?.end || new Date()))) {
									onFilterChange(column.id, { start: date ? date.toISOString().split("T")[0] : null, end: endDate });
								}
							}}
							dateFormat="dd/MM/yyyy"
							placeholderText={`Inicio`}
							showYearDropdown
							showMonthDropdown
							dropdownMode="select"
							locale="es"
						/>
						<DatePicker
							className="filter-datepicker"
							selected={column.dateRangeState?.end || null}
							onChange={(date) => {
								column.dateRangeState?.setEnd(date);
								const startDate = column.dateRangeState?.start ? column.dateRangeState.start.toISOString().split("T")[0] : null;

								if (!startDate || (date && date >= (column.dateRangeState?.start || new Date()))) {
									onFilterChange(column.id, { start: startDate, end: date ? date.toISOString().split("T")[0] : null });
								}
							}}
							dateFormat="dd/MM/yyyy"
							placeholderText={`Fin`}
							showYearDropdown
							showMonthDropdown
							dropdownMode="select"
							locale="es"
							disabled={!column.dateRangeState?.start} // Deshabilita si no hay fecha de inicio
						/>
					</div>
				);
			case FILTER_TYPES.AMOUNT_RANGE:
				const selectedOption = column.quantityRangeState?.option || ""; // Cambiado a una sola opción
				const quantity = column.quantityRangeState?.quantity || 0;

				const handleFilterChange = () => {
					if (selectedOption) {
						onFilterChange(column.id, { option: selectedOption, quantity });
					}
				};

				return (
					<AmountRangeComponent
						selectedOption={selectedOption} // Se pasa solo una opción
						onOptionChange={(value: string) => {
							column.quantityRangeState?.setOption(value);
						}}
						quantity={quantity}
						filterOptions={column.filterOptions}
						onQuantityChange={(value: number) => {
							column.quantityRangeState?.setQuantity(value);
						}}
						onFilterChange={handleFilterChange}
					/>
				);
			default: // No filter
				return null;
		}
	}

	/*
		TELEFONO: una tarjeta por fila.

		Ocho columnas no caben en 390px por mucho que se aprieten. Cada fila pasa a
		ser una tarjeta con el mismo lenguaje que el resto de la app (waLt*): la
		primera columna con etiqueta hace de titulo y las demas van como pares
		etiqueta/dato. Las columnas SIN etiqueta (la casilla de seleccionar) se
		quedan arriba a la derecha, donde se alcanzan con el pulgar.

		El contenido sale de las MISMAS columnas que usa la tabla, asi que no hay
		una segunda version que mantener. (Esteban, 2026-09-20)
	*/
	if (tarjetasEnTelefono && esTelefono) {
		const valorDe = (columna: Column, fila: any) =>
			columna.render ? columna.render(fila) : (columna.accessor ? fila[columna.accessor] : null);
		const conEtiqueta = columns.filter((c: Column) => c.header !== "" && c.header != null && !c.enCabeceraEnTelefono);
		const sinEtiqueta = columns.filter((c: Column) => c.header === "" || c.header == null);
		const enCabecera  = columns.filter((c: Column) => c.enCabeceraEnTelefono);
		const marcada     = conEtiqueta.filter((c: Column) => c.tituloEnTelefono)[0];
		const titulo      = marcada || conEtiqueta[0];
		const campos      = conEtiqueta.filter((c: Column) => c !== titulo);

		if (!data || data.length < 1) {
			return <div className="waLtVacio">Sin registros</div>;
		}

		return (
			<div className="waLt">{
				data.map((fila: any, i: number) => (
					<article className="waLtTarjeta" key={"tarjeta-" + i}>
						<header className="waLtCabecera">
							<span className="waLtTitulo">{
								titulo
									? (titulo.renderTituloEnTelefono ? titulo.renderTituloEnTelefono(fila) : valorDe(titulo, fila))
									: ""
							}</span>
							<span className="waLtCabeceraLado">
								{enCabecera.map((c: Column) => (
									<span className="waLtCabeceraDato" key={c.id}>
										<span className="waLtCabeceraEtiqueta">{c.header}</span>
										<span className="waLtCabeceraValor">{valorDe(c, fila)}</span>
									</span>
								))}
								{sinEtiqueta.map((c: Column) => (
									<span key={c.id}>{valorDe(c, fila)}</span>
								))}
							</span>
						</header>
						<dl className="waLtCampos">{
							campos.map((c: Column) => (
								<div className="waLtCampo" key={c.id}>
									<dt>{c.header}</dt>
									<dd>{valorDe(c, fila)}</dd>
								</div>
							))
						}</dl>
						{
							/* Los avisos de "no se puede cobrar" viven en renderSubRow: en la
							   tabla van como una fila debajo, aqui al pie de la tarjeta. */
							columns.map((c: Column) => c.renderSubRow ? c.renderSubRow(fila) : null)
								.filter((x: any) => x)
								.map((x: any, j: number) => <div className="waLtNota" key={"nota-" + j}>{x}</div>)
						}
					</article>
				))
			}</div>
		);
	}

	return (
		<div className="table-wrapper">
			<table className={tableClass}>
				<thead>
					{isAnyFilterActive() ?
						<OverlayTrigger
							overlay={
								<Tooltip id="clear-all-filter">
									Limpiar todos los filtros
								</Tooltip>
							}>
							<button
								onClick={handleClearAllFilterColumn}
								style={{
									color: "var(--wa-acento)",
									padding: "10px",
									background: "var(--wa-papel)",
									height: "44px"
								}}>
								<FontAwesomeIcon
									icon={solid("times")}
									size="xl"
								/>
							</button>
						</OverlayTrigger>
						: <React.Fragment />
					}
					<tr key="commonRow"
						style={{ color: "var(--wa-acento)" }}>
						{columns.map((column, index) => {
							const headerStyle: React.CSSProperties = column.overrideHeaderStyle ? column.overrideHeaderStyle(data) : {};
							/*
								El encabezado hereda el ancho que la columna ya declara para sus
								celdas. En Pagos los anchos viven en overrideCellStyle y el
								encabezado se habia quedado sin ninguno: Chromium lo salva
								partiendo el titulo en dos renglones, pero otros motores
								(Safari) aplastan la celda por debajo del texto y los titulos
								se montan unos sobre otros. Reportado por Karen, 2026-09-21.

								Solo se copia si el encabezado NO trae ancho propio, asi que
								ninguna pantalla que ya lo declare cambia.
							*/
							if(headerStyle.width == null && headerStyle.minWidth == null && column.overrideCellStyle)
							{
								/*
									Con try: overrideCellStyle lo escribe cada pantalla y varias
									leen campos de la fila. Aqui se llama sin fila para averiguar
									el ancho, asi que una que lea algo anidado tiraria la tabla
									entera. Si falla, se sigue sin ancho, como antes.
								*/
								try
								{
									const anchoDeLaColumna = column.overrideCellStyle({}).width

									/*
										Va "width" ademas de "minWidth": cuando la tabla usa
										anchos fijos (table-layout:fixed, como Pagos) el navegador
										reparte segun la PRIMERA fila, o sea el encabezado. Con
										solo minWidth le daba a todas el mismo ancho y cortaba el
										contenido. (2026-09-21)
									*/
									if(anchoDeLaColumna != null)
									{
										headerStyle.width = anchoDeLaColumna
										headerStyle.minWidth = anchoDeLaColumna
									}
								}
								catch(error) { /* la columna necesita su fila: se deja como estaba */ }
							}
							const alineacionTitulo =
								(headerStyle.textAlign === "left" || headerStyle.textAlign === "start")
									? "izquierda"
									: ((headerStyle.textAlign === "right" || headerStyle.textAlign === "end")
										? "derecha"
										: "centro");
							return (
								<th
									key={index}
									className={column.overrideHeaderClasses}
									style={{
										borderBottom: column.header === "" ? 0 : "1px solid var(--wa-gris)",
										...headerStyle
									}}>
									<div style={{
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										width: "100%"
									}}>
										{/*
											El titulo se centraba siempre, aunque el contenido de la
											columna fuera a la izquierda, y las dos cosas no cuadraban.
											Ahora sigue el textAlign que traiga overrideHeaderStyle;
											sin el se queda centrado, como estaba.
										*/}
										<div style={{
											display: "flex",
											/*
												A la izquierda se invierte la fila para que el titulo quede
												pegado al borde y el embudo del filtro se vaya detras: si no,
												el embudo empuja la palabra unos 44px y sigue sin cuadrar con
												el contenido de la columna.
											*/
											flexDirection: alineacionTitulo === "izquierda" ? "row-reverse" : "row",
											justifyContent: alineacionTitulo === "centro" ? "center" : "flex-end",
											alignItems: "center",
											gap: "10px",
											width: "100%",
											/*
												Antes: minWidth "max-content", o sea el titulo NUNCA se
												partia y cada columna quedaba obligada a medir lo que mide
												su titulo entero. Con catorce columnas eso empujaba la
												tabla de Pagos a 1.830px y el personal tenia que arrastrar
												de lado en cualquier laptop de 11-12".

												Ahora el titulo puede partirse en dos renglones y la
												columna se queda con el ancho que declara. El embudo no
												encoge, para que no se monte sobre el texto.
												(Esteban, 2026-09-21)
											*/
											minWidth: 0
										}}>
											{column.filterType &&
												<OverlayTrigger
													overlay={
														<Tooltip id={`${column.id}-filter`}>
															{filterStates[column.id]
																? "Limpiar"
																: `Filtrar por ${column.header}`
															}
														</Tooltip>
													}>
													<button
														onClick={() => handleClearFilterColumn(column.id)}
														style={{
															color: "var(--wa-acento)",
															padding: "10px",
															background: "var(--wa-papel)",
															height: "44px"
														}}>
														<FontAwesomeIcon
															icon={filterStates[column.id] ? solid("times") : solid("filter")}
														/>
													</button>
												</OverlayTrigger>
											}
											{column.header}
										</div>
										{filterStates[column.id] && renderFilterType(
											column.filterType ?? 0,
											column,
											filters,
											onFilterChange
										)}
									</div>
								</th>
							);
						})}
					</tr>
				</thead>
				<tbody>
					{data && data.length > 0
						? data.map((rowData, rowIndex) => (
							<React.Fragment key={`rowFragment${rowIndex}`}>
								<tr
									key={`commonTableRow${rowIndex}`}
									onClick={() => onRowClick && onRowClick(rowData)}
									style={{ cursor: onRowClick ? "pointer" : "default" }}
								>
									{columns.map((column, colIndex) => {
										const cellStyle = column.overrideCellStyle ? column.overrideCellStyle(rowData) : {};
										const cellClassNames = column.overrideCellClasses ? column.overrideCellClasses : "";
										return (
											<td
												className={cellClassNames}
												style={{ textAlign: "left", whiteSpace: "nowrap", ...cellStyle }}
												key={colIndex}
											>
												{column.render ? column.render(rowData) : rowData[column.accessor]}
											</td>
										);
									})}
								</tr>
								{/* Render if column has renderSubRow property*/}
								{columns.map((column, colIndex) =>
									column.renderSubRow && column.renderSubRow(rowData) ? (
										<tr key={`subRow${rowIndex}-${colIndex}`}>
											<td colSpan={columns.length} style={{ padding: 0, border: 0 }}>
												<div style={{ padding: "8px" }}>
													{column.renderSubRow(rowData)}
												</div>
											</td>
										</tr>
									) : null
								)}
							</React.Fragment>
						))
						: <React.Fragment />
					}
				</tbody>
			</table>
			{data && data.length === 0
				? <div
					style={{
						width: "100%",
						padding: "10px 10px 0px 10px",
						textAlign: "center",
						boxSizing: "border-box"
					}}>
					<h3 style={{
						width: "100%",
						padding: "10px 0",
						textAlign: "center"
					}}>
						No se encontraron registros.
					</h3>
				</div>
				: <React.Fragment />
			}
		</div>
	);
};

export default CommonTable;
